import React, { useState } from 'react';
import { Zap } from 'lucide-react';

interface AiAssistButtonProps {
  onClick: () => Promise<void>;
  label?: string;
  loadingLabel?: string;
  size?: 'sm' | 'md';
  className?: string;
}

const AiAssistButton: React.FC<AiAssistButtonProps> = ({
  onClick,
  label = 'AI',
  loadingLabel = '...',
  size = 'sm',
  className = '',
}) => {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);
    setDone(false);
    try {
      await onClick();
      setDone(true);
      setTimeout(() => setDone(false), 2000);
    } catch (err) {
      console.error('AI assist error:', err);
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = size === 'sm'
    ? 'px-2 py-1 text-[12px] gap-1'
    : 'px-2.5 py-1.5 text-xs gap-1.5';

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={`inline-flex items-center ${sizeClasses} rounded-lg font-medium transition-all
        ${done
          ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
          : 'bg-accent-50 dark:bg-accent-900/20 text-accent-600 dark:text-accent-400 border border-accent-200 dark:border-accent-800 hover:bg-accent-100 dark:hover:bg-accent-900/40'
        }
        disabled:opacity-50 ${className}`}
    >
      {loading ? (
        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : (
        <Zap size={size === 'sm' ? 10 : 12} />
      )}
      <span>{loading ? loadingLabel : done ? 'Klart!' : label}</span>
    </button>
  );
};

export default AiAssistButton;
